import { Share, MoreVertical, PlusSquare } from 'lucide-react'
import { InstallTexts, resolveTexts } from './install.types'

/**
 * AP-17-E — Plattform-spezifische Schritt-für-Schritt-Anleitungen zum
 * Installieren, geteilt von ERP und Stempel-PWA.
 *
 * Genutzt von `InstallButton` (Fallback-Modal, wenn der Browser keinen nativen
 * Ein-Klick-Install anbietet — iOS-Safari, Firefox) und von `InstallGuide`
 * (aufklappbar im Profil).
 *
 * Die Schritte sind in beiden Apps dieselben; unterschiedlich sind nur die
 * Wörter aus `InstallTexts` — siehe dort.
 */

function StepList({ children }: { children: React.ReactNode }) {
  return <ol className="space-y-3 text-sm text-foreground">{children}</ol>
}

function Step({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-3">
      <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold">
        {n}
      </span>
      <span>{children}</span>
    </li>
  )
}

// ── Desktop / sonstige Browser ───────────────────────────────────────────────

export function OtherSteps({ texts }: { texts: InstallTexts }) {
  const { openTarget } = resolveTexts(texts)
  return (
    <StepList>
      <Step n={1}>
        Öffne {openTarget} in <span className="font-medium">Chrome</span> oder{' '}
        <span className="font-medium">Edge</span>
      </Step>
      <Step n={2}>
        Klicke auf das <span className="font-medium">Installations-Symbol</span> rechts in der
        Adressleiste (Monitor mit Pfeil) – oder im Browser-Menü auf{' '}
        <span className="font-medium">„App installieren"</span>
      </Step>
      <Step n={3}>
        Bestätige mit <span className="font-medium">„Installieren"</span>
      </Step>
    </StepList>
  )
}

// ── iOS (Safari) ─────────────────────────────────────────────────────────────

export function IOSSteps({ texts }: { texts: InstallTexts }) {
  const { shareBarAtBottom } = resolveTexts(texts)
  return (
    <StepList>
      <Step n={1}>
        Tippe auf das{' '}
        <span className="inline-flex items-center gap-1 font-medium">
          Teilen-Symbol <Share size={14} className="inline text-primary" />
        </span>{' '}
        in der Safari-Menüleiste{shareBarAtBottom ? ' unten' : ''}
      </Step>
      <Step n={2}>
        Wähle{' '}
        <span className="inline-flex items-center gap-1 font-medium">
          „Zum Home-Bildschirm" <PlusSquare size={14} className="inline text-primary" />
        </span>{' '}
        aus dem Menü
      </Step>
      <Step n={3}>
        Bestätige mit <span className="font-medium">„Hinzufügen"</span> oben rechts
      </Step>
    </StepList>
  )
}

// ── Android (Chrome/Edge) – Fallback wenn kein beforeinstallprompt ────────────

export function AndroidSteps() {
  return (
    <StepList>
      <Step n={1}>
        Tippe auf das{' '}
        <span className="inline-flex items-center gap-1 font-medium">
          Menü <MoreVertical size={14} className="inline text-primary" />
        </span>{' '}
        (drei Punkte) oben rechts im Browser
      </Step>
      <Step n={2}>
        Wähle <span className="font-medium">„Zum Startbildschirm hinzufügen"</span>
      </Step>
      <Step n={3}>
        Bestätige mit <span className="font-medium">„Hinzufügen"</span>
      </Step>
    </StepList>
  )
}

/** Wählt die passende Anleitung anhand der erkannten Plattform. */
export function InstallSteps({
  platform,
  texts,
}: {
  platform: 'ios' | 'android' | 'other'
  texts: InstallTexts
}) {
  if (platform === 'ios') return <IOSSteps texts={texts} />
  if (platform === 'android') return <AndroidSteps />
  return <OtherSteps texts={texts} />
}
